import images from '@assets/images/index'
import { Button } from '@components/ui'
import { useDataLoader } from '@hooks/useDataLoader'
import { useSlugLinks } from '@hooks/useSlugLinks'
import { useQuery } from '@tanstack/react-query'
import { generateModifiedContent } from '@utils/assessmentReportUtils'
import { Flex, Input, Radio, message } from 'antd'
import { useEffect, useState } from 'react'
import { Cluster, Grade, Module, SearchResult } from 'src/types/type'

import { useFramework } from '@suntex.fim/framework'
import { SendMessage } from '@suntex.fim/messages'
import { useTeamInfo } from '@suntex.fim/team'
import { LoaderOverlay } from '@suntex.fim/ui'

import { capitalizeFirstLetter } from '../AssessmentReport/AssessmentReportNavigation'
import { handleEnterOrSpacePress } from '../LearningCenter/util'

import GradeDetailsPage from './GradeDetailsPage'
import ModuleDetailsPage from './ModuleDetailsPage'
import RenderGradeContent from './RenderGradeContent'
import RenderModuleContent from './RenderModuleContent'
import {
  getModuleDisplayName,
  getModuleUrl,
  parseModuleReference
} from './moduleDisplayName'

function isGrade(result: SearchResult): result is Grade {
  return (result as Grade).grade !== undefined
}
function isModule(result: SearchResult): result is Module {
  return (result as Module).module !== undefined
}
function Correlations() {
  const { api } = useFramework()
  // Call the custom hook
  const {
    selectedCluster,
    grade,
    gradeData,
    selectedModule,
    moduleData,
    selectedCorrelation,
    correlationData,
    setSelectedCluster,
    setGrade,
    setStrands,
    setSelectedModule,
    setSelectedCorrelation,
    defaultGrade
  } = useDataLoader()

  const { slugLinks, isLoading } = useSlugLinks()
  // custom function to convert string to number
  function convertString(str: string | number) {
    // First, check if the string is purely numeric (not NaN when converted to a number)
    if (!Number.isNaN(Number(str))) {
      return Number(str) // Convert numeric string to a number
    }
    return str
  }
  const team = useTeamInfo()
  const { data: selectedUserOptions = [] } = useQuery({
    queryKey: ['assessment', 'summaryTeam', team?.id],
    queryFn: async () => {
      const response = await api.get_Assessment_SummaryTeam({
        entityId: team!.id
      })
      return (response.playerSummaries || []).map((player) => ({
        label: player.displayName,
        value: player.id
      }))
    },
    enabled: team?.id != null
  })
  const [SuggestContentModal, setSuggestContentModal] = useState<boolean>(false)
  const [mode, setMode] = useState<string>('grade') // Default mode
  // Define the state as an object where keys are combition of module name and cluster name, and values are booleans (checked status)
  const [checkedModulesAndClusters, setCheckedModulesAndClusters] = useState<{
    [key: string]: boolean
  }>({})
  const [searchQuery, setSearchQuery] = useState('')

  const [filteredResults, setFilteredResults] = useState<SearchResult[]>([])
  // Handle change when a checkbox is toggled
  const handleCheckboxChange = (name: string) => {
    // Update the state for the clicked checkbox (toggle its checked value)
    setCheckedModulesAndClusters((prevState) => ({
      ...prevState,
      [name]: !prevState[name] // Toggle the checked value for the current module
    }))
  }
  const handleClusterClick = (cluster: Cluster) => {
    if (selectedCluster.name !== cluster.name) {
      setSelectedCluster(cluster)
      setStrands(cluster.strands)
      const correlationGrade = correlationData.filter(
        (correlationData: Grade) =>
          correlationData.grade === convertString(grade[0])
      )
      if (correlationGrade[0]) {
        const correlationCluster = correlationGrade[0].clusters.find(
          (c) => c.name === cluster.name
        )
        if (correlationCluster) {
          setSelectedCorrelation(correlationCluster)
        }
      }
    }
  }
  const handleSelectModuleClick = (module: Module) => {
    setSelectedModule(module)
  }

  // Reset to original data if searchQuery is cleared or on initial load
  useEffect(() => {
    if (searchQuery === '') {
      if (
        mode === 'grade' &&
        gradeData.length > 0 &&
        correlationData.length > 0
      ) {
        setFilteredResults(gradeData)
        setGrade(defaultGrade)
        setSelectedCluster(gradeData[0].clusters[0])
        setSelectedCorrelation(correlationData[0].clusters[0])
      } else if (mode === 'module' && moduleData.length > 0) {
        setFilteredResults(moduleData)
        setSelectedModule(moduleData[0])
      }
    }
  }, [searchQuery, gradeData, mode, moduleData]) // Depend on gradeData as well in case it's updated later

  useEffect(() => {
    const getData = setTimeout(() => {
      if (searchQuery.trim() === '') {
        if (mode === 'grade') {
          setFilteredResults(gradeData) // Reset to gradeData if search is empty
        } else if (mode === 'module') {
          setFilteredResults(moduleData) // Reset to moduleData if search is empty
        }
        return
      }
      const modifySearchQuery = searchQuery
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
      const results: SearchResult[] = []
      if (mode === 'grade') {
        gradeData.forEach((gradeData) => {
          const grade = `Grade ${gradeData.grade}`
          // Check if searchQuery matches the grade
          if (grade.toLowerCase().includes(modifySearchQuery)) {
            results.push(gradeData) // If grade matches, push all clusters for that grade
          } else {
            // If grade doesn't match, filter clusters by matching the cluster name
            const matchingClusters = gradeData.clusters.filter((cluster) =>
              cluster.name.toLowerCase().includes(modifySearchQuery)
            )
            if (matchingClusters.length > 0) {
              results.push({
                grade: gradeData.grade,
                clusters: matchingClusters
              })
            }
          }
        })
      } else if (mode === 'module') {
        // Search within modulesData
        moduleData.forEach((module) => {
          // Check if searchQuery matches the module.module field
          const moduleMatches = module.module
            .toLowerCase()
            .includes(modifySearchQuery)

          if (moduleMatches) {
            results.push(module) // Push the entire module if it matches
          }
        })
      }
      if (results.length > 0 && mode === 'grade') {
        const firstResult = results[0]

        if (isGrade(firstResult)) {
          const filterGrade = (results as Grade[]).filter(
            (gradeData: Grade) =>
              gradeData.grade === convertString(firstResult.grade)
          )

          const correlationGrade = correlationData.filter(
            (correlationData: Grade) =>
              correlationData.grade === convertString(firstResult.grade)
          )

          if (correlationGrade.length > 0) {
            setSelectedCorrelation(correlationGrade[0].clusters[0])
          }

          if (filterGrade.length > 0) {
            setSelectedCluster(filterGrade[0].clusters[0])
          }

          setGrade(firstResult.grade)
        }
      }

      if (results.length > 0 && mode === 'module') {
        const firstResult = results[0]

        if (isModule(firstResult)) {
          setSelectedModule(firstResult) // Safe type
        }
      }

      setFilteredResults(results)
    }, 500)

    return () => clearTimeout(getData)
  }, [searchQuery, gradeData, mode, moduleData])

  /**
   * sets mode state
   */
  const handleModeChange = (e: any) => {
    setSearchQuery('')
    setMode(e.target.value)
    if (e.target.value === 'grade') {
      setFilteredResults(gradeData)
      setGrade(defaultGrade)
      setSelectedCluster(gradeData[0].clusters[0])
      setSelectedCorrelation(correlationData[0].clusters[0])
    } else {
      setFilteredResults(moduleData)
      setSelectedModule(moduleData[0])
    }
  }
  const onChangeGrade = (key: string | string[]) => {
    let data: Grade[] = []
    if (searchQuery.trim().length > 0) {
      data = filteredResults as Grade[]
    } else {
      data = gradeData
    }
    if (key.length === 1) {
      const filterGrade = data.filter(
        (gradeData: Grade) => gradeData.grade === convertString(key[0])
      )
      const correlationGrade = correlationData.filter(
        (correlationData: Grade) =>
          correlationData.grade === convertString(key[0])
      )
      if (correlationGrade.length > 0) {
        setSelectedCorrelation(correlationGrade[0].clusters[0])
      }
      if (filterGrade.length > 0) {
        setSelectedCluster(filterGrade[0].clusters[0])
      }
      setGrade(key)
    } else {
      setGrade(grade)
    }
  }
  // Function to check if any value in the object is true
  const isAnyModuleOrClustersCheckboxChecked = Object.values(
    checkedModulesAndClusters
  ).some((value) => value === true)
  // Function to check if the module matches with any of the slugs
  const getModuleContent = (moduleSlug: string) => {
    // Remove square brackets if they exist
    const cleanSlug = moduleSlug.replace(/[[\]]/g, '')
    const matchingItem = slugLinks.find((item) => item.slug === cleanSlug)
    if (matchingItem) {
      // Use the original module URL for Hub-style navigation (same as UIUtil.navigate)
      const moduleUrl = matchingItem.urlNew.urlPreview || ''

      const handleModuleClick = () => {
        if (moduleUrl) {
          // Legacy modules render Adobe Animate/createjs canvases that need a
          // full page reload rather than client-side routing.
          window.location.href = moduleUrl
        }
      }

      return (
        <div
          onClick={handleModuleClick}
          className="text-primary-hover"
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleModuleClick()
            }
          }}
          aria-label={`Open module ${matchingItem.text}`}
        >
          {getModuleDisplayName(matchingItem.text, cleanSlug)}
        </div>
      )
    }
    return <div>{moduleSlug} - Module not found</div>
  }
  // Function to check if the module matches with any of the slugs
  const getMessageContent = (moduleSlug: string) => {
    // Remove square brackets if they exist
    const cleanReference = moduleSlug.replace(/[[\]]/g, '')
    const moduleReference = parseModuleReference(cleanReference)
    const matchingItem = slugLinks.find(
      (item) => item.slug === moduleReference.slug
    )
    if (matchingItem) {
      // Use the original module URL for Hub-style navigation (same as UIUtil.navigate)
      const moduleUrl = getModuleUrl(matchingItem.urlClassic.url, {
        suggestionKey: matchingItem.urlClassic.hash,
        moduleVariant: moduleReference.variant
      })
      return { url: moduleUrl, text: matchingItem.text || '' }
    }
    return { url: '', text: cleanReference || '' }
  }

  const convertLinksToMarkdown = (html: string) => {
    // Create a DOMParser to parse the HTML string
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    // Find all <a> elements
    const links = doc.querySelectorAll('a')

    // Map each link to the Markdown format
    const markdownLinks = Array.from(links).map((link) => {
      const href = link.getAttribute('href')
      const text = link.textContent?.trim() || ''

      // Custom transformation for text (if needed)
      const formattedText = text.replace('Bonus Games', 'Bonus 1')

      // Return the formatted Markdown link
      return `[${formattedText}](${href})`
    })

    // Join the links with new lines
    return markdownLinks.join('\n')
  }
  const copyToClipboard = async () => {
    const content = generateModifiedContent(
      checkedModulesAndClusters,
      getMessageContent
    )
    if (content) {
      const text = content.replaceAll('<br>', '\n')
      const markdown = convertLinksToMarkdown(text)
      if (!markdown) return
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(markdown)
        message.success(
          'Your module has been copied. Now you can paste it into the message.'
        )
      }
    }
  }
  if (isLoading) {
    return <LoaderOverlay isLoading={isLoading} />
  }
  return (
    <Flex className="suggest-content suggest-content-wrapper">
      <>
        <div className="suggest-content__sidebar">
          <Flex
            vertical
            gap="10px"
            tabIndex={0}
            onKeyDown={(e) => {
              handleEnterOrSpacePress(e)
            }}
          >
            <Radio.Group
              onChange={handleModeChange}
              value={mode}
              className="suggest-content__sidebar-switch"
              aria-label="Select content suggestion mode"
            >
              <Radio.Button
                value="grade"
                aria-label="Suggest by Grade"
                tabIndex={0}
                aria-checked={mode === 'grade'}
              >
                By Grade
              </Radio.Button>
              <Radio.Button
                value="module"
                aria-label="Suggest by Module"
                tabIndex={0}
                aria-checked={mode === 'module'}
              >
                By Module
              </Radio.Button>
            </Radio.Group>
            <Input
              placeholder="Search..."
              prefix={<images.SearchIcon />}
              className="suggest-content__sidebar-search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search modules or grades"
              tabIndex={0}
              role="searchbox"
            />
            {/* Side bar content */}
            {mode === 'grade' ? (
              <RenderGradeContent
                filteredResults={filteredResults}
                grade={grade}
                selectedCluster={selectedCluster}
                onChangeGrade={onChangeGrade}
                handleClusterClick={handleClusterClick}
                searchQuery={searchQuery}
                checkedModulesAndClusters={checkedModulesAndClusters}
              />
            ) : (
              <RenderModuleContent
                filteredResults={filteredResults}
                selectedModule={selectedModule}
                handleSelectModuleClick={handleSelectModuleClick}
                checkedModulesAndClusters={checkedModulesAndClusters}
              />
            )}
          </Flex>
        </div>
        {/* Main content */}
        <div className="suggest-content__main">
          <div className="suggest-content__main-table">
            <Flex
              className="section-title"
              align="center"
              justify="space-between"
              wrap
            >
              <h4>Suggest Content by {capitalizeFirstLetter(mode)}</h4>
              <Flex
                gap="10px"
                justify="space-between"
                wrap
                className="suggest-content-btn"
              >
                <Button
                  intent="tertiary"
                  disabled={!isAnyModuleOrClustersCheckboxChecked}
                  onClick={copyToClipboard}
                  aria-label="Copy selected modules or clusters to clipboard"
                  tabIndex={0}
                  role="button"
                  onKeyDown={(e) =>
                    handleEnterOrSpacePress(e, () => {
                      if (!isAnyModuleOrClustersCheckboxChecked) return
                      copyToClipboard()
                    })
                  }
                >
                  Copy to Clipboard
                </Button>
                <Button
                  intent="tertiary"
                  onClick={() => setSuggestContentModal(true)}
                  disabled={!isAnyModuleOrClustersCheckboxChecked}
                  aria-label="Send message to selected users"
                  tabIndex={0}
                  role="button"
                  onKeyDown={(e) =>
                    handleEnterOrSpacePress(e, () => {
                      if (!isAnyModuleOrClustersCheckboxChecked) return
                      setSuggestContentModal(true)
                    })
                  }
                >
                  SEND MESSAGE
                </Button>
                <SendMessage
                  isModalOpen={SuggestContentModal}
                  closeModal={() => {
                    setSuggestContentModal(false)
                    setCheckedModulesAndClusters({})
                  }}
                  selectedUserOptions={selectedUserOptions}
                  recipientEntityId={team?.id}
                  gameContent={checkedModulesAndClusters}
                  mode={mode}
                  getMessageContent={getMessageContent}
                />
              </Flex>
            </Flex>
            {/* Correlation card for BY GRADE Tab */}
            {mode === 'grade' && (
              <GradeDetailsPage
                selectedCluster={selectedCluster}
                grade={grade}
                checkedModulesAndClusters={checkedModulesAndClusters}
                handleCheckboxChange={handleCheckboxChange}
                selectedCorrelation={selectedCorrelation}
                getModuleContent={getModuleContent}
              />
            )}

            {/* Correlation card for BY MODULE Tab */}
            {mode === 'module' && (
              <ModuleDetailsPage
                selectedModule={selectedModule}
                checkedModulesAndClusters={checkedModulesAndClusters}
                handleCheckboxChange={handleCheckboxChange}
                slugLinks={slugLinks}
              />
            )}
          </div>
        </div>
      </>
    </Flex>
  )
}

export default Correlations
